/**
 * API 请求模块
 *
 * 优化说明：
 * 1. 使用环境变量管理 API 地址
 * 2. 使用统一的 request 工具（带 token 加密存储、自动过期处理、操作日志）
 * 3. 保持与后端接口路径完全兼容
 */

import request, { API_BASE_URL, ASSETS_BASE_URL } from '../utils/request'

// 导出 API 基础地址，供特殊场景使用
export { API_BASE_URL, ASSETS_BASE_URL }

// ==================== 认证相关 API ====================
export const authAPI = {
  // 登录
  login: (data: { username: string; password: string }) =>
    request.post('/auth/admin/login', data),

  // 获取当前管理员信息
  getAdminInfo: () => request.get('/auth/admin/info'),

  // 修改密码
  changePassword: (data: { oldPassword: string; newPassword: string }) =>
    request.put('/auth/admin/password', data),

  // 退出登录
  logout: () => request.post('/auth/admin/logout'),
}

// ==================== 仪表盘 API ====================
export const dashboardAPI = {
  // 获取仪表盘数据
  getStats: () => request.get('/dashboard/stats'),

  // 获取销售趋势
  getSalesTrend: (params: { range: string; startDate?: string; endDate?: string }) =>
    request.get('/dashboard/sales-trend', { params }),

  // 获取待办工单
  getPendingTasks: () => request.get('/dashboard/pending-tasks'),

  // 获取最近订单
  getRecentOrders: (limit?: number) =>
    request.get('/dashboard/recent-orders', { params: { limit } }),
}

// ==================== 商品相关 API ====================
export const productAPI = {
  // 获取商品列表
  getList: (params?: {
    page?: number
    pageSize?: number
    keyword?: string
    categoryId?: number
    status?: string
  }) => request.get('/products', { params }),

  // 获取商品详情
  getDetail: (id: number) => request.get(`/products/${id}`),

  // 创建商品
  create: (data: any) => request.post('/products', data),

  // 更新商品
  update: (id: number, data: any) => request.put(`/products/${id}`, data),

  // 删除商品
  delete: (id: number) => request.delete(`/products/${id}`),

  // 批量删除
  batchDelete: (ids: number[]) => request.post('/products/batch-delete', { ids }),

  // 上下架商品
  toggleStatus: (id: number, status: boolean) =>
    request.patch(`/products/${id}/status`, { status }),

  // 批量上下架
  batchToggleStatus: (ids: number[], status: boolean) =>
    request.post('/products/batch-status', { ids, status }),
}

// ==================== 分类相关 API ====================
export const categoryAPI = {
  // 获取分类列表
  getList: (params?: { level?: number; parentId?: number }) =>
    request.get('/categories', { params }),

  // 获取分类树
  getTree: () => request.get('/products/categories/tree'),

  // 创建分类
  create: (data: any) => request.post('/categories', data),

  // 更新分类
  update: (id: number, data: any) => request.put(`/categories/${id}`, data),

  // 删除分类
  delete: (id: number) => request.delete(`/categories/${id}`),
}

// ==================== 价格日期 API ====================
export const priceAPI = {
  // 获取SKU列表（已上架商品）
  getList: (params?: {
    page?: number
    pageSize?: number
    keyword?: string
    categoryId?: number
    stockStatus?: string
  }) => request.get('/price/skus', { params }),

  // 获取SKU详情
  getDetail: (id: number) => request.get(`/price/skus/${id}`),

  // 批量保存价格和库存
  batchSave: (data: any[]) => request.post('/price/batch-save', { skus: data }),

  // 获取价格历史
  getPriceHistory: (skuId: number) => request.get(`/price/history/${skuId}`),

  // 获取促销方案列表
  getPromotions: () => request.get('/price/promotions'),

  // 同步商品数据
  syncProducts: () => request.post('/price/sync'),
}

// ==================== 商品证件 API ====================
export const productCertAPI = {
  // 获取证件列表
  getList: (params?: {
    page?: number
    pageSize?: number
    keyword?: string
    productId?: number
    certType?: string
  }) => request.get('/product-certs', { params }),

  // 创建证件
  create: (data: any) => request.post('/product-certs', data),

  // 更新证件
  update: (id: number, data: any) => request.put(`/product-certs/${id}`, data),

  // 删除证件
  delete: (id: number) => request.delete(`/product-certs/${id}`),

  // 切换状态
  toggleStatus: (id: number, status: boolean) =>
    request.patch(`/product-certs/${id}/status`, { status }),

  // 下载证件
  download: (id: number) =>
    request.get(`/product-certs/${id}/download`, { responseType: 'blob' as const }),
}

// ==================== 订单相关 API ====================
export const orderAPI = {
  // 获取订单列表
  getList: (params?: {
    page?: number
    pageSize?: number
    keyword?: string
    status?: string
    startDate?: string
    endDate?: string
  }) => request.get('/orders', { params }),

  // 获取订单详情
  getDetail: (id: number) => request.get(`/orders/${id}`),

  // 更新订单状态
  updateStatus: (id: number, status: string) =>
    request.patch(`/orders/${id}/status`, { status }),

  // 取消订单
  cancel: (id: number, reason: string) =>
    request.post(`/orders/${id}/cancel`, { reason }),

  // 审核支付凭证
  verifyPayment: (id: number, result: 'verified' | 'rejected', remark?: string) =>
    request.post(`/orders/${id}/verify-payment`, { result, remark }),

  // 发货
  deliver: (
    id: number,
    data: { logisticsCompany: string; trackingNumber: string; remark?: string }
  ) => request.post(`/orders/${id}/deliver`, data),

  // 导出订单
  export: (params: { startDate: string; endDate: string; fields: string[] }) =>
    request.post('/orders/export', params, { responseType: 'blob' as const }),
  // 删除订单（仅已取消的订单可删除）
  delete: (id: number) => request.delete(`/orders/${id}`),
}

// ==================== 会员相关 API ====================
export const memberAPI = {
  // 获取会员列表
  getList: (params?: {
    page?: number
    pageSize?: number
    keyword?: string
    memberType?: string
    level?: string
    role?: string
  }) => request.get('/members', { params }),

  // 获取会员详情
  getDetail: (id: number) => request.get(`/members/${id}`),

  // 创建会员
  create: (data: any) => request.post('/members', data),

  // 更新会员
  update: (id: number, data: any) => request.put(`/members/${id}`, data),

  // 删除会员
  delete: (id: number) => request.delete(`/members/${id}`),

  // 切换状态
  toggleStatus: (id: number, status: boolean) =>
    request.patch(`/members/${id}/status`, { status }),

  // 修改等级
  updateLevel: (id: number, level: string) =>
    request.patch(`/members/${id}/level`, { level }),

  // 重置密码
  resetPassword: (id: number, password: string) =>
    request.post(`/members/${id}/reset-password`, { password }),

  // 更新地址
  updateAddress: (id: number, address: any) =>
    request.put(`/members/${id}/address`, address),

  // 关联门店
  updateStores: (id: number, storeIds: number[]) =>
    request.post(`/members/${id}/stores`, { storeIds }),

  // 审核会员
  audit: (id: number, data: { status: string; auditRemark: string }) =>
    request.patch(`/members/${id}/audit`, data),
}

// ==================== 门店相关 API ====================
export const storeAPI = {
  // 获取门店列表
  getList: (params?: { page?: number; pageSize?: number; keyword?: string }) =>
    request.get('/stores', { params }),

  // 创建门店
  create: (data: any) => request.post('/stores', data),

  // 更新门店
  update: (id: number, data: any) => request.put(`/stores/${id}`, data),

  // 删除门店
  delete: (id: number) => request.delete(`/stores/${id}`),
}

// ==================== 优惠券相关 API ====================
export const couponAPI = {
  // 获取优惠券列表
  getList: (params?: { page?: number; pageSize?: number; status?: string }) =>
    request.get('/coupons', { params }),

  // 创建优惠券
  create: (data: any) => request.post('/coupons', data),

  // 更新优惠券
  update: (id: number, data: any) => request.put(`/coupons/${id}`, data),

  // 删除优惠券
  delete: (id: number) => request.delete(`/coupons/${id}`),

  // 切换状态
  toggleStatus: (id: number, status: boolean) =>
    request.patch(`/coupons/${id}/status`, { status }),

  // 发放优惠券
  grant: (id: number, data: { userIds: number[]; quantity: number }) =>
    request.post(`/coupons/${id}/grant`, data),
}

// ==================== 数据报表 API ====================
export const statisticsAPI = {
  // 获取销售概览
  getOverview: (params: { startDate: string; endDate: string }) =>
    request.get('/statistics/overview', { params }),

  // 获取销售趋势
  getSalesTrend: (params: { startDate: string; endDate: string; range: string }) =>
    request.get('/statistics/sales-trend', { params }),

  // 获取商品排行
  getProductRanking: (params: { startDate: string; endDate: string; sortBy: string }) =>
    request.get('/statistics/product-ranking', { params }),

  // 获取客户排行
  getCustomerRanking: (params: { startDate: string; endDate: string }) =>
    request.get('/statistics/customer-ranking', { params }),

  // 获取门店排行
  getStoreRanking: (params: { startDate: string; endDate: string }) =>
    request.get('/statistics/store-ranking', { params }),

  // 获取分类占比
  getCategoryRatio: (params: { startDate: string; endDate: string }) =>
    request.get('/statistics/category-ratio', { params }),
}

// ==================== 系统设置 API ====================

// 首页配置
export const homeConfigAPI = {
  // 获取配置
  getConfig: () => request.get('/settings/home'),

  // 保存配置
  saveConfig: (data: any) => request.post('/settings/home', data),

  // 轮播图管理
  getBanners: () => request.get('/settings/home/banners'),
  createBanner: (data: any) => request.post('/settings/home/banners', data),
  updateBanner: (id: number, data: any) => request.put(`/settings/home/banners/${id}`, data),
  deleteBanner: (id: number) => request.delete(`/settings/home/banners/${id}`),

  // 公告管理
  getNotices: () => request.get('/settings/home/notices'),
  createNotice: (data: any) => request.post('/settings/home/notices', data),
  updateNotice: (id: number, data: any) => request.put(`/settings/home/notices/${id}`, data),
  deleteNotice: (id: number) => request.delete(`/settings/home/notices/${id}`),

  // 推荐商品
  getRecommendProducts: () => request.get('/settings/home/recommend-products'),
  createRecommendProduct: (data: any) =>
    request.post('/settings/home/recommend-products', data),
  updateRecommendProduct: (id: number, data: any) =>
    request.put(`/settings/home/recommend-products/${id}`, data),
  deleteRecommendProduct: (id: number) =>
    request.delete(`/settings/home/recommend-products/${id}`),
}

// 配送与运费
export const deliveryAPI = {
  // 配送区域
  getAreas: () => request.get('/settings/delivery/areas'),
  createArea: (data: any) => request.post('/settings/delivery/areas', data),
  updateArea: (id: number, data: any) => request.put(`/settings/delivery/areas/${id}`, data),
  deleteArea: (id: number) => request.delete(`/settings/delivery/areas/${id}`),
  toggleAreaStatus: (id: number, status: boolean) =>
    request.patch(`/settings/delivery/areas/${id}/status`, { status }),

  // 运费规则
  getFreightRules: () => request.get('/settings/delivery/freight-rules'),
  createFreightRule: (data: any) => request.post('/settings/delivery/freight-rules', data),
  updateFreightRule: (id: number, data: any) =>
    request.put(`/settings/delivery/freight-rules/${id}`, data),
  deleteFreightRule: (id: number) => request.delete(`/settings/delivery/freight-rules/${id}`),
  toggleFreightRuleStatus: (id: number, status: boolean) =>
    request.patch(`/settings/delivery/freight-rules/${id}/status`, { status }),
}

// 管理员与角色
export const adminAPI = {
  // 管理员
  getAdmins: (params?: { page?: number; pageSize?: number; keyword?: string }) =>
    request.get('/settings/admins', { params }),
  createAdmin: (data: any) => request.post('/settings/admins', data),
  updateAdmin: (id: number, data: any) => request.put(`/settings/admins/${id}`, data),
  deleteAdmin: (id: number) => request.delete(`/settings/admins/${id}`),
  toggleAdminStatus: (id: number, status: boolean) =>
    request.patch(`/settings/admins/${id}/status`, { status }),
  resetAdminPassword: (id: number, password: string) =>
    request.post(`/settings/admins/${id}/reset-password`, { password }),

  // 角色
  getRoles: () => request.get('/settings/roles'),
  createRole: (data: any) => request.post('/settings/roles', data),
  updateRole: (id: number, data: any) => request.put(`/settings/roles/${id}`, data),
  deleteRole: (id: number) => request.delete(`/settings/roles/${id}`),
  toggleRoleStatus: (id: number, status: boolean) =>
    request.patch(`/settings/roles/${id}/status`, { status }),
}

// 公司证件
export const companyCertAPI = {
  getCompanyCert: () => request.get('/settings/certs/company'),
  saveCompanyCert: (data: { imageUrl: string }) =>
    request.post('/settings/certs/company', data),
}

// 操作日志
export const logAPI = {
  getList: (params?: {
    page?: number
    pageSize?: number
    startDate?: string
    endDate?: string
    module?: string
    action?: string
    keyword?: string
  }) => request.get('/settings/logs', { params }),

  getDetail: (id: number) => request.get(`/settings/logs/${id}`),
  export: (params: any) =>
    request.post('/settings/logs/export', params, { responseType: 'blob' as const }),
  clear: () => request.delete('/settings/logs/clear'),
}

// 数据备份
export const backupAPI = {
  getRecords: (params?: { page?: number; pageSize?: number }) =>
    request.get('/settings/backup/records', { params }),
  getStats: () => request.get('/settings/backup/stats'),
  getConfig: () => request.get('/settings/backup/config'),
  saveConfig: (data: any) => request.post('/settings/backup/config', data),
  create: (data: any) => request.post('/settings/backup/create', data),
  restore: (id: number) => request.post(`/settings/backup/restore/${id}`),
  download: (id: number) =>
    request.get(`/settings/backup/download/${id}`, { responseType: 'blob' as const }),
  delete: (id: number) => request.delete(`/settings/backup/${id}`),
  uploadRestore: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return request.post('/settings/backup/upload-restore', formData)
  },
}

// ==================== 上传相关 API ====================
export const uploadAPI = {
  // 上传图片
  uploadImage: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return request.post('/upload/image', formData)
  },

  // 上传文件
  uploadFile: (file: File, type?: string) => {
    const formData = new FormData()
    formData.append('file', file)
    if (type) formData.append('type', type)
    return request.post('/upload/file', formData)
  },
}

// 默认导出（向后兼容）
export default request
